(function feeloriCollectionAnalyticsModule(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.FeelOriCollectionAnalytics = api;
  api.start(root);
})(typeof window !== 'undefined' ? window : globalThis, function createFeelOriCollectionAnalytics() {
  'use strict';

  const GA4_DESTINATION = 'G-QW7V2C0S90';

  function cardToItem(card, listContext) {
    const data = card.dataset || {};
    const priceCents = Number.parseInt(data.feeloriPriceCents, 10);

    return {
      item_id: data.feeloriItemId || data.feeloriProductId,
      item_name: data.feeloriProductTitle,
      item_brand: data.feeloriVendor || 'FeelOri',
      item_category: data.feeloriProductType || '',
      item_list_id: listContext.item_list_id,
      item_list_name: listContext.item_list_name,
      index: Number.parseInt(data.feeloriIndex, 10),
      price: Number.isFinite(priceCents) ? priceCents / 100 : 0,
      quantity: 1,
    };
  }

  function buildListPayload(grid) {
    if (!grid) return null;

    const data = grid.dataset || {};
    const listContext = {
      item_list_id: data.feeloriItemListId || '',
      item_list_name: data.feeloriItemListName || '',
    };
    const cards = Array.from(grid.querySelectorAll('.feelori-v2-card[data-feelori-product-id]'));

    return {
      ...listContext,
      currency: data.feeloriCurrency || '',
      items: cards.map((card) => cardToItem(card, listContext)),
    };
  }

  function sendEvent(runtime, eventName, payload) {
    if (!runtime || typeof runtime.gtag !== 'function') return false;

    const eventPayload = {
      ...payload,
      send_to: GA4_DESTINATION,
    };
    if (runtime.Shopify?.theme?.role === 'unpublished') eventPayload.debug_mode = true;

    runtime.gtag('event', eventName, eventPayload);
    return true;
  }

  function hasQueuedEvent(dataLayer, eventName) {
    if (!Array.isArray(dataLayer)) return false;

    return dataLayer.some((entry) => {
      if (entry && entry.event === eventName) return true;
      if (!entry || typeof entry.length !== 'number') return false;

      const args = Array.from(entry);
      return args[0] === 'event' && args[1] === eventName;
    });
  }

  function listDigest(payload) {
    return JSON.stringify([
      payload.item_list_id,
      payload.items.map((item) => item.item_id),
    ]);
  }

  function createTracker(runtime) {
    const sentListDigests = new Set();

    function trackList({ respectExisting = false } = {}) {
      const grid = runtime.document && runtime.document.getElementById('product-grid');
      const payload = buildListPayload(grid);
      if (!payload || payload.items.length === 0) return 'empty';

      const digest = listDigest(payload);
      if (sentListDigests.has(digest)) return 'duplicate';

      if (respectExisting && hasQueuedEvent(runtime.dataLayer, 'view_item_list')) {
        sentListDigests.add(digest);
        return 'existing';
      }

      if (!sendEvent(runtime, 'view_item_list', payload)) return 'unavailable';

      sentListDigests.add(digest);
      return 'sent';
    }

    function trackSelection(target) {
      if (!target || typeof target.closest !== 'function') return 'ignored';

      const excluded = target.closest(
        '[data-feelori-save-product], product-form, form, button, input, select, textarea'
      );
      if (excluded) return 'ignored';

      const card = target.closest('.feelori-v2-card[data-feelori-product-id]');
      if (!card) return 'ignored';

      const link = target.closest('a[href]');
      if (link) {
        const href = link.getAttribute('href') || '';
        if (!href.includes('/products/')) return 'ignored';
      } else {
        const interactive = target.closest('a, button, input, select, textarea, product-form, form');
        const isMobileCardTap = !interactive
          && typeof runtime.matchMedia === 'function'
          && runtime.matchMedia('(max-width: 749px)').matches;
        if (!isMobileCardTap) return 'ignored';
      }

      const grid = (typeof card.closest === 'function' && card.closest('#product-grid'))
        || (runtime.document && runtime.document.getElementById('product-grid'));
      const listPayload = buildListPayload(grid);
      if (!listPayload) return 'empty';

      const listContext = {
        item_list_id: listPayload.item_list_id,
        item_list_name: listPayload.item_list_name,
      };
      const payload = {
        ...listContext,
        currency: listPayload.currency,
        items: [cardToItem(card, listContext)],
      };

      return sendEvent(runtime, 'select_item', payload) ? 'sent' : 'unavailable';
    }

    return {
      trackList,
      trackSelection,
    };
  }

  function start(runtime) {
    if (!runtime || !runtime.document) return 'no-document';
    if (runtime.__feeloriCollectionAnalyticsTracker) return 'already-started';

    const tracker = createTracker(runtime);
    const document = runtime.document;
    const setTimer = typeof runtime.setTimeout === 'function'
      ? runtime.setTimeout.bind(runtime)
      : setTimeout;
    const clearTimer = typeof runtime.clearTimeout === 'function'
      ? runtime.clearTimeout.bind(runtime)
      : clearTimeout;
    let gridTimer;

    runtime.__feeloriCollectionAnalyticsTracker = tracker;

    function runInitial(attempt = 0) {
      const result = tracker.trackList({ respectExisting: true });
      if (result === 'unavailable' && attempt < 6) {
        setTimer(() => runInitial(attempt + 1), 500);
      }
    }

    function scheduleGridUpdate() {
      if (gridTimer) clearTimer(gridTimer);
      gridTimer = setTimer(() => {
        gridTimer = null;
        tracker.trackList({ respectExisting: false });
      }, 150);
    }

    function setup() {
      document.addEventListener('click', (event) => {
        tracker.trackSelection(event.target);
      }, true);

      const container = document.getElementById('ProductGridContainer');
      if (container && typeof runtime.MutationObserver === 'function') {
        const observer = new runtime.MutationObserver(scheduleGridUpdate);
        observer.observe(container, { childList: true });
      }

      if (document.readyState === 'complete') {
        setTimer(runInitial, 750);
      } else if (typeof runtime.addEventListener === 'function') {
        runtime.addEventListener('load', () => setTimer(runInitial, 750), { once: true });
      } else {
        setTimer(runInitial, 750);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setup, { once: true });
    } else {
      setup();
    }

    return 'started';
  }

  return {
    buildListPayload,
    createTracker,
    hasQueuedEvent,
    sendEvent,
    start,
  };
});
